import { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { Layout } from '@components/Layout/Layout';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import { AuthLoadingPage } from '@pages/AuthLoadingPage';
import { InviteAcceptPage } from '@pages/InviteAcceptPage';
import { DashboardPage } from '@pages/DashboardPage';
import { ApprovalsPage } from '@pages/ApprovalsPage';
import { RecordsPage } from '@pages/RecordsPage';
import { LegacyRecordRedirect } from '@pages/LegacyRecordRedirect';
import { ProfilePage } from '@pages/ProfilePage';
import { AdminSettingsPage } from '@pages/AdminSettingsPage';
import { OrgStructurePage } from '@pages/OrgStructurePage';
import { NotificationsPage } from '@pages/NotificationsPage';
import { MemoriesPage } from '@pages/MemoriesPage';
import { ApprovalChainsPage } from './pages/ApprovalChainsPage';
import { OnboardingPage } from '@pages/OnboardingPage';
import { PaymentMethodStep } from '@components/Onboarding/steps/PaymentMethodStep';
import { PaymentReturnPage } from '@pages/PaymentReturnPage';
import { VendorDetailsPage } from '@pages/VendorDetailsPage';
import VendorRegisterPage from '@pages/VendorRegisterPage';
import VendorProfilePage from '@pages/VendorProfilePage';
import VendorCustomersPage from '@pages/VendorCustomersPage';
import VendorInvoicesPage from '@pages/VendorInvoicesPage';
import { BillingSettingsPage } from '@pages/BillingSettingsPage';
import { ChatPage } from '@pages/ChatPage';
import { AdminRoute } from '@components/common/AdminRoute';
import { FeatureGate } from '@components/billing/FeatureGate';
import { useCheckAuth } from '@api/hooks/useCheckAuth';
import { useGetErpConnection } from '@api/hooks/useErpConnection';
import { fetchErpAuthStatus, getErpProviderLabel, isErpAuthProvider } from '@api/quickbooksAuth';
import { isTenantAdminUser, useAuthStore } from '@store/authStore';

// The backend redirects here on SSO failures (`/login?error=...` from the
// login callback, `/settings/sso?error=...` from the admin-consent callback).
// Neither path is a real app route, so translate them to the actual routes
// while preserving the query string — a bare `<Navigate to="/auth" />` via
// the wildcard route below would otherwise drop the `error` param entirely.
const LoginRedirect = () => {
  const location = useLocation();
  return <Navigate to={{ pathname: '/auth', search: location.search }} replace />;
};

const SsoSettingsRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', 'sso');
  return <Navigate to={{ pathname: '/admin/settings', search: `?${params.toString()}` }} replace />;
};

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const AppBootstrap = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const qbCheckKeyRef = useRef<string | null>(null);
  const [isCheckingQuickBooks, setIsCheckingQuickBooks] = useState(false);
  const { currentUser, activeTenantId, isAuthenticated } = useAuthStore();
  const effectiveTenantId = activeTenantId ?? currentUser?.active_tenant_id ?? null;
  const tenantAdmin = isTenantAdminUser(currentUser);
  const isPublicVendorRegistrationRoute = location.pathname === '/register/vendor';
  const isInviteAcceptRoute = location.pathname === '/invite/accept';
  // These are transient redirect targets (see LoginRedirect/SsoSettingsRedirect
  // above) — skip auth bootstrapping here so it doesn't race with, and clobber,
  // the query-preserving Navigate those routes perform.
  const isLoginRedirectRoute = location.pathname === '/login';
  const isSsoSettingsRedirectRoute = location.pathname === '/settings/sso';
  const shouldBootstrap =
    location.pathname !== '/auth' &&
    !isPublicVendorRegistrationRoute &&
    !isInviteAcceptRoute &&
    !isLoginRedirectRoute &&
    !isSsoSettingsRedirectRoute;
  const { isLoading: isCheckingAuth } = useCheckAuth(shouldBootstrap);

  const {
    data: erpConnection,
    isLoading: isLoadingErpConnection,
  } = useGetErpConnection(shouldBootstrap && isAuthenticated && !isCheckingAuth);

  useEffect(() => {
    qbCheckKeyRef.current = null;
    setIsCheckingQuickBooks(false);
  }, [effectiveTenantId]);

  useEffect(() => {
    if (!shouldBootstrap || isCheckingAuth) {
      return;
    }

    if (!isAuthenticated) {
      const redirectTarget = `${location.pathname}${location.search}${location.hash}`;
      const authTarget = redirectTarget && redirectTarget !== '/'
        ? `/auth?redirect=${encodeURIComponent(redirectTarget)}`
        : '/auth';
      navigate(authTarget, { replace: true });
    }
  }, [isAuthenticated, isCheckingAuth, location.hash, location.pathname, location.search, navigate, shouldBootstrap]);

  useEffect(() => {
    if (!shouldBootstrap || isCheckingAuth || !isAuthenticated) {
      setIsCheckingQuickBooks(false);
      return;
    }

    if (isLoadingErpConnection) {
      return;
    }

    if (!tenantAdmin || !erpConnection?.realm_id || !isErpAuthProvider(erpConnection.erp_type)) {
      qbCheckKeyRef.current = null;
      setIsCheckingQuickBooks(false);
      return;
    }
    const authProvider = erpConnection.erp_type;

    const qbCheckKey = `${currentUser?.id ?? 'unknown-user'}:${effectiveTenantId ?? 'no-tenant'}:${erpConnection.realm_id}`;
    if (qbCheckKeyRef.current === qbCheckKey) {
      setIsCheckingQuickBooks(false);
      return;
    }

    qbCheckKeyRef.current = qbCheckKey;
    let isActive = true;
    setIsCheckingQuickBooks(true);

    void (async () => {
      try {
        const authStatus = await fetchErpAuthStatus(authProvider, {
          activeTenantId: effectiveTenantId,
        });
        if (!isActive) {
          return;
        }

        if (!authStatus.is_authenticated && authStatus.authorization_url) {
          window.location.href = authStatus.authorization_url;
        }
      } catch (error) {
        console.error('[AppBootstrap] Failed to preflight QuickBooks auth status:', error);
      } finally {
        if (isActive) {
          setIsCheckingQuickBooks(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [
    erpConnection?.realm_id,
    erpConnection?.erp_type,
    isAuthenticated,
    isCheckingAuth,
    isLoadingErpConnection,
    currentUser?.id,
    effectiveTenantId,
    shouldBootstrap,
    tenantAdmin,
  ]);

  if (shouldBootstrap && (isCheckingAuth || (isAuthenticated && (isLoadingErpConnection || isCheckingQuickBooks)))) {
    const providerLabel = getErpProviderLabel(erpConnection?.erp_type);
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#060e1f] p-4">
        <LoadingSpinner text={isCheckingQuickBooks ? `Checking ${providerLabel}…` : 'Verifying session…'} />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/auth" element={<AuthLoadingPage />} />
      <Route path="/login" element={<LoginRedirect />} />
      <Route path="/settings/sso" element={<SsoSettingsRedirect />} />
      <Route path="/invite/accept" element={<InviteAcceptPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/onboarding/payment" element={<PaymentMethodStep />} />
      <Route path="/onboarding/payment/return" element={<PaymentReturnPage />} />
      <Route path="/chat" element={<ChatPage />} />

      <Route element={<Layout />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/approvals" element={<ApprovalsPage />} />
        <Route path="/records" element={<RecordsPage />} />
        <Route path="/upload" element={<LegacyRecordRedirect />} />
        <Route path="/reviews" element={<LegacyRecordRedirect />} />
        <Route path="/ap-postings" element={<LegacyRecordRedirect />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/policies" element={<MemoriesPage />} />
        <Route path="/memories" element={<Navigate to="/policies" replace />} />
        <Route path="/vendors" element={<LegacyRecordRedirect />} />
        <Route path="/vendors/new" element={<LegacyRecordRedirect />} />
        <Route path="/vendors/:vendorId/edit" element={<LegacyRecordRedirect />} />
        <Route path="/records/vendors/new" element={<FeatureGate feature="vendors"><VendorDetailsPage /></FeatureGate>} />
        <Route path="/records/vendors/:vendorId/edit" element={<FeatureGate feature="vendors"><VendorDetailsPage /></FeatureGate>} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/settings/billing" element={<BillingSettingsPage />} />
        {/* Vendor portal routes (vendor-tier plan) */}
        <Route path="/vendor-profile" element={<VendorProfilePage />} />
        <Route path="/vendor-customers" element={<VendorCustomersPage />} />
        <Route path="/vendor-invoices" element={<VendorInvoicesPage />} />
        <Route
          path="/admin/settings"
          element={(
            <AdminRoute
              requiredPermission={[
                'user.read',
                'user.write',
                'role.read',
                'role.write',
                'org_unit.write',
                'approval_chain.write',
              ]}
            >
              <AdminSettingsPage />
            </AdminRoute>
          )}
        />
        <Route
          path="/org-structure"
          element={(
            <FeatureGate feature="org_units">
              <AdminRoute requiredPermission={['org_unit.read', 'org_unit.write']}>
                <OrgStructurePage />
              </AdminRoute>
            </FeatureGate>
          )}
        />
        <Route
          path="/approval-chain"
          element={(
            <FeatureGate feature="approvals">
              <AdminRoute requiredPermission="approval_chain.write">
                <ApprovalChainsPage />
              </AdminRoute>
            </FeatureGate>
          )}
        />
      </Route>

      {/* Vendor self-registration (public — no auth required) */}
      <Route path="/register/vendor" element={<VendorRegisterPage />} />

      <Route path="*" element={<Navigate to="/auth" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppBootstrap />
      </Router>
    </QueryClientProvider>
  );
}

export default App;
